import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Users, Briefcase, ChevronUp, ChevronDown } from 'lucide-react';
import { AVP, LoanOfficer, Loan } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
interface AvpDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  avp: AVP | null;
  loanOfficers: LoanOfficer[];
}
const STATUS_STYLES: Record<string, string> = {
  Closed: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
  Funded: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300',
  'In Underwriting': 'bg-amber-500/10 border-amber-500/20 text-amber-300'
};
type LOSortField = 'name' | 'loansClosed' | 'loanVolume' | 'totalRevenue';
type LoanSortField = 'borrower' | 'product' | 'closeDate' | 'status' | 'amount' | 'revenue';
type SortDir = 'asc' | 'desc';
function SortHeader({
  label,
  field,
  activeField,
  direction,
  onSort,
  align = 'left'
}: {
  label: string;
  field: string;
  activeField: string;
  direction: SortDir;
  onSort: (field: any) => void;
  align?: 'left' | 'right';
}) {
  const isActive = field === activeField;
  return <button onClick={() => onSort(field)} className={`group inline-flex items-center gap-1 text-xs uppercase ${isActive ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300'} transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`} data-id="element-19">
      <span data-id="element-20">{label}</span>
      {isActive ? direction === 'asc' ? <ChevronUp className="w-3 h-3 text-indigo-400" data-id="element-21" /> : <ChevronDown className="w-3 h-3 text-indigo-400" data-id="element-22" /> : <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" data-id="element-23" />}
    </button>;
}
function compare(a: any, b: any, dir: SortDir) {
  if (typeof a === 'string' && typeof b === 'string') {
    return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }
  return dir === 'asc' ? a - b : b - a;
}
function LoansTable({
  loans
}: {
  loans: Loan[];
}) {
  const [sortField, setSortField] = useState<LoanSortField>('amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSort = (field: LoanSortField) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const sorted = useMemo(() => {
    return [...loans].sort((a, b) => compare(a[sortField], b[sortField], sortDir));
  }, [loans, sortField, sortDir]);
  return <div className="rounded-lg overflow-hidden bg-black/20 border border-white/5" data-id="element-24">
      <table className="w-full text-left" data-id="element-25">
        <thead data-id="element-26">
          <tr className="border-b border-white/5" data-id="element-27">
            <th className="py-2 px-3" data-id="element-28">
              <SortHeader label="Borrower" field="borrower" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-29" />
            </th>
            <th className="py-2 px-3" data-id="element-30">
              <SortHeader label="Product" field="product" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-31" />
            </th>
            <th className="py-2 px-3" data-id="element-32">
              <SortHeader label="Date" field="closeDate" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-33" />
            </th>
            <th className="py-2 px-3" data-id="element-34">
              <SortHeader label="Status" field="status" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-35" />
            </th>
            <th className="py-2 px-3 text-right" data-id="element-36">
              <SortHeader label="Amount" field="amount" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-37" />
            </th>
            <th className="py-2 px-3 text-right" data-id="element-38">
              <SortHeader label="Revenue" field="revenue" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-39" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5" data-id="element-40">
          {sorted.map(loan => <tr key={loan.id} data-id="element-41">
              <td className="py-2 px-3 text-sm text-white" data-id="element-42">{loan.borrower}</td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-43">
                {loan.product}
              </td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-44">
                {loan.closeDate}
              </td>
              <td className="py-2 px-3" data-id="element-45">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[loan.status]}`} data-id="element-46">
                  {loan.status}
                </span>
              </td>
              <td className="py-2 px-3 text-sm text-white text-right" data-id="element-47">
                {formatCurrency(loan.amount)}
              </td>
              <td className="py-2 px-3 text-sm text-emerald-400 text-right" data-id="element-48">
                {formatCurrency(loan.revenue)}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
export function AvpDetailModal({
  isOpen,
  onClose,
  avp,
  loanOfficers
}: AvpDetailModalProps) {
  const [expandedLO, setExpandedLO] = useState<string | null>(null);
  const [loSortField, setLoSortField] = useState<LOSortField>('totalRevenue');
  const [loSortDir, setLoSortDir] = useState<SortDir>('desc');
  useEffect(() => {
    setExpandedLO(null);
  }, [avp?.id]);
  const sortedLOs = useMemo(() => {
    return [...loanOfficers].sort((a, b) => compare(a[loSortField], b[loSortField], loSortDir));
  }, [loanOfficers, loSortField, loSortDir]);
  if (!avp) return null;
  const handleLoSort = (field: LOSortField) => {
    if (field === loSortField) {
      setLoSortDir(loSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setLoSortField(field);
      setLoSortDir('desc');
    }
  };
  const toggleLO = (id: string) => {
    setExpandedLO(expandedLO === id ? null : id);
  };
  return <AnimatePresence data-id="element-49">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-50" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-51">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-8 max-w-4xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-52">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-53">
                <div className="flex items-center gap-4" data-id="element-54">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-200 font-bold text-lg" data-id="element-55">
                    {avp.initials}
                  </div>
                  <div data-id="element-56">
                    <h2 className="text-2xl font-bold text-white" data-id="element-57">
                      {avp.name}
                    </h2>
                    <p className="text-sm text-slate-400" data-id="element-58">
                      {avp.division} Division · Rank #{avp.rank}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-59">
                  <X className="w-5 h-5" data-id="element-60" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-white/5" data-id="element-61">
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-62">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-63">Loan Volume</p>
                  <p className="text-lg font-bold text-white" data-id="element-64">
                    {formatCurrency(avp.loanVolume, true)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-65">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-66">Total Revenue</p>
                  <p className="text-lg font-bold text-emerald-400" data-id="element-67">
                    {formatCurrency(avp.totalRevenue, true)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-68">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-69">Loans Closed</p>
                  <p className="text-lg font-bold text-white" data-id="element-70">
                    {formatNumber(avp.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-71">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-72">Loan Officers</p>
                  <p className="text-lg font-bold text-white" data-id="element-73">{avp.loCount}</p>
                </div>
              </div>

              {/* LO Accordion */}
              <div className="p-6 max-h-[480px] overflow-y-auto" data-id="element-74">
                <div className="flex items-center justify-between mb-4" data-id="element-75">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-76">
                    <Users className="w-4 h-4 text-indigo-400" data-id="element-77" /> Loan Officers
                  </h3>
                  <span className="text-xs text-slate-500" data-id="element-78">
                    Click a row to view loans
                  </span>
                </div>

                <div className="space-y-2" data-id="element-79">
                  {/* Sortable column header */}
                  <div className="grid grid-cols-[24px_1fr_80px_110px_110px] gap-3 px-4 py-2 border-b border-white/5" data-id="element-80">
                    <span data-id="element-81"></span>
                    <SortHeader label="Officer" field="name" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} data-id="element-82" />
                    <SortHeader label="Loans" field="loansClosed" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-83" />
                    <SortHeader label="Volume" field="loanVolume" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-84" />
                    <SortHeader label="Revenue" field="totalRevenue" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-85" />
                  </div>

                  {sortedLOs.map(lo => {
                const isExpanded = expandedLO === lo.id;
                return <div key={lo.id} className={`rounded-xl border overflow-hidden transition-colors ${isExpanded ? 'bg-white/[0.04] border-white/15' : 'bg-white/[0.02] border-white/5 hover:border-white/10'}`} data-id="element-86">
                        <button onClick={() => toggleLO(lo.id)} className="w-full grid grid-cols-[24px_1fr_80px_110px_110px] gap-3 items-center px-4 py-3 text-left" data-id="element-87">
                          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} data-id="element-88" />
                          <div className="flex items-center gap-3 min-w-0" data-id="element-89">
                            <div className="w-8 h-8 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-200 text-xs font-semibold shrink-0" data-id="element-90">
                              {lo.initials}
                            </div>
                            <div className="min-w-0" data-id="element-91">
                              <p className="text-sm text-white font-medium truncate" data-id="element-92">
                                {lo.name}
                              </p>
                              <p className="text-xs text-slate-500 truncate" data-id="element-93">
                                Loan Officer
                              </p>
                            </div>
                          </div>
                          <span className="text-sm text-slate-300 text-right" data-id="element-94">
                            {formatNumber(lo.loansClosed)}
                          </span>
                          <span className="text-sm text-white text-right" data-id="element-95">
                            {formatCurrency(lo.loanVolume, true)}
                          </span>
                          <span className="text-sm text-emerald-400 text-right" data-id="element-96">
                            {formatCurrency(lo.totalRevenue, true)}
                          </span>
                        </button>

                        <AnimatePresence initial={false} data-id="element-97">
                          {isExpanded && <motion.div key="content" initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeOut'
                    }} className="overflow-hidden" data-id="element-98">
                              <div className="px-4 pb-4 pt-1 border-t border-white/5" data-id="element-99">
                                <div className="flex items-center gap-2 text-xs uppercase text-slate-500 mb-2 mt-3" data-id="element-100">
                                  <Briefcase className="w-3.5 h-3.5" data-id="element-101" />{' '}
                                  Contributing Loans
                                </div>
                                <LoansTable loans={lo.loans} data-id="element-102" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/5 flex items-center justify-end bg-black/10" data-id="element-103">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/10" data-id="element-104">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}